import { useRef } from "react";

import { SINGULAR, PLURAL } from "neetocommons/constants";
import { captureAnalyticsEvent } from "neetocommons/utils";
import { Pane, Toastr, Typography } from "neetoui";
import { ActionBlock, Form, Input } from "neetoui/formik";
import { Trans, useTranslation } from "react-i18next";

import {
  LABEL_REJECT_CHARS_REGEX,
  TAXONOMY_EVENTS,
  VALIDATION_SCHEMA,
} from "./constants";
import { useUpdateTaxonomy } from "./useTaxonomyApi";
import { formatLabel } from "./utils";

const EditPane = ({
  columnLabels,
  columnsToShow,
  initialValues,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const initialFocusRef = useRef(null);

  const { mutate: updateTaxonomy } = useUpdateTaxonomy();

  const {
    singularColumn: showSingularInputField = true,
    pluralColumn: showPluralInputField = true,
  } = columnsToShow;

  const {
    singular: singularLabel = t("neetoMolecules.taxonomy.singularLabel"),
    plural: pluralLabel = t("neetoMolecules.taxonomy.pluralLabel"),
  } = columnLabels;

  const handleSubmit = (values, { setSubmitting }) => {
    const { keyName, singularLabel, pluralLabel } = values;

    const payload = {
      taxonomy: {
        keyName,
        singularLabel: formatLabel(singularLabel),
        pluralLabel: formatLabel(pluralLabel),
      },
    };

    const options = {
      onSuccess: () => {
        captureAnalyticsEvent(TAXONOMY_EVENTS.UPDATED, {
          taxonomy: payload.taxonomy,
        });
        window.location.reload();
      },
      onError: error => Toastr.error(error),
      onSettled: () => setSubmitting(false),
    };

    updateTaxonomy({ keyName, payload }, options);
  };

  return (
    <Pane
      {...{ initialFocusRef, isOpen, onClose }}
      data-testid="taxonomy-edit-pane"
    >
      <Pane.Header>
        <Typography style="h2" weight="semibold">
          <Trans
            i18nKey="neetoMolecules.taxonomy.editPane.title"
            values={{
              entity: t(
                `taxonomyDefaultLabels.${initialValues.keyName}.singular`
              ),
              ...(showSingularInputField && showPluralInputField
                ? PLURAL
                : SINGULAR),
            }}
          />
        </Typography>
      </Pane.Header>
      <Form
        formikProps={{
          initialValues,
          validationSchema: VALIDATION_SCHEMA,
          onSubmit: handleSubmit,
        }}
      >
        <Pane.Body>
          <div className="flex w-full flex-col gap-y-6">
            {showSingularInputField && (
              <Input
                required
                className="w-full"
                data-testid="taxonomy-singular-input-field"
                label={singularLabel}
                name="singularLabel"
                ref={initialFocusRef}
                rejectCharsRegex={LABEL_REJECT_CHARS_REGEX}
              />
            )}
            {showPluralInputField && (
              <Input
                required
                className="w-full"
                data-testid="taxonomy-plural-input-field"
                label={pluralLabel}
                name="pluralLabel"
                rejectCharsRegex={LABEL_REJECT_CHARS_REGEX}
              />
            )}
          </div>
        </Pane.Body>
        <Pane.Footer>
          <ActionBlock
            cancelButtonProps={{ onClick: onClose, disabled: false }}
            className="flex items-center gap-x-2"
          />
        </Pane.Footer>
      </Form>
    </Pane>
  );
};

export default EditPane;
